// ======================================================
// FILE: src/components/ProfilePage.js   (PRODUCTION)
// ======================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { saveUserProfileSafe } from "../utils/profileSaveSafe";
import { useAccess } from "../access/AccessContext";
import { PLAN_CATALOG, getPlanFromProfile, getStorageLimitBytes } from "../lib/planLimits";
import { clearSessionBypass, loadMfaProfile, saveMfaProfile } from "../utils/mfa";
const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * DAY_MS;

function centsToCurrency(amount, currency = "EUR") {
  const formatter = new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  });
  return formatter.format((amount || 0) / 100);
}

function timestampToMs(ts) {
  if (!ts) return 0;
  if (typeof ts === "number") return ts;
  if (typeof ts?.toMillis === "function") return ts.toMillis();
  if (typeof ts?.seconds === "number") return ts.seconds * 1000;
  if (typeof ts?._seconds === "number") return ts._seconds * 1000;
  return 0;
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = Math.max(0, Number(bytes || 0));
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  const digits = n >= 10 || i < 2 ? 0 : 1;
  return `${n.toFixed(digits)} ${u[i]}`;
}

function useOptionalAccess() {
  try {
    const ctx = useAccess();
    return ctx || { readOnly: false };
  } catch {
    return { readOnly: false };
  }
}

function VerifyEmailBanner() {
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  async function resend() {
    setBusy(true);
    setErr("");
    try {
      const { getAuth } = await import("firebase/auth");
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in.");
      const token = await user.getIdToken();
      const res = await fetch("/api/sendFreeWelcomeEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Could not send email.");
      setSent(true);
    } catch (e) {
      setErr(e.message || "Could not send email. Please try again.");
    }
    setBusy(false);
  }

  return (
    <div className="pf-alert" style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
      <div>Please verify your email address. Check your inbox for a verification link from LastWish Box.</div>
      {sent ? (
        <div style={{ color: "#065f46", fontWeight: 600 }}>Verification email sent - check your inbox.</div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={resend}
            disabled={busy}
            style={{ padding: "7px 16px", borderRadius: 8, background: "#980000", color: "#fff", fontWeight: 700, fontSize: 13, border: "none", cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1 }}
          >
            {busy ? "Sending..." : "Resend verification email"}
          </button>
          {err && <span style={{ color: "#b91c1c", fontSize: 13 }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

export default function ProfilePage({ user, onUpdate, onLogout }) {
  const { readOnly } = useOptionalAccess();
  const location = useLocation();
  const navigate = useNavigate();

  const uid = user?.uid;

  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [usage, setUsage] = useState({ storageBytes: 0, objectCount: 0, updatedAt: null });
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetErr, setResetErr] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeBusy, setUpgradeBusy] = useState("");
  const [upgradeErr, setUpgradeErr] = useState("");
  const [subscriptionState, setSubscriptionState] = useState({
    cancelAt: user?.subscription?.cancelAt || null,
    cancelAtPeriodEnd: !!user?.subscription?.cancelAtPeriodEnd,
    currentPeriodEnd: user?.subscription?.currentPeriodEnd || null,
    status: user?.subscription?.status || user?.subscriptionStatus || "",
  });

  // --- Account deletion UI state ---
  const [showDel, setShowDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelMsg, setCancelMsg] = useState("");
  const [cancelErr, setCancelErr] = useState("");
  const [showRetentionOffer, setShowRetentionOffer] = useState(false);
  const [retentionBusy, setRetentionBusy] = useState("");
  const [retentionErr, setRetentionErr] = useState("");

  // --- MFA UI state ---
  const [mfaState, setMfaState] = useState({ loading: true, enabled: false });
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaErr, setMfaErr] = useState("");
  const [mfaMsg, setMfaMsg] = useState("");
 

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || user.displayName || "",
      email: user.email || user.username || "",
      phone: user.phone || user.phoneNumber || "",
      address: user.address || "",
    });
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location?.search || "");
    const hash = (location?.hash || "").toLowerCase();
    if (params.get("upgrade") === "1" || params.get("upgrade") === "true" || hash === "#upgrade") {
      setUpgradeOpen(true);
    }
  }, [location]);

  useEffect(() => {
    setSubscriptionState({
      cancelAt: user?.subscription?.cancelAt || null,
      cancelAtPeriodEnd: !!user?.subscription?.cancelAtPeriodEnd,
      currentPeriodEnd: user?.subscription?.currentPeriodEnd || null,
      status: user?.subscription?.status || user?.subscriptionStatus || "",
    });
  }, [user]);
  const fetchUsage = useCallback(async () => {
    const cu = auth.currentUser;
    if (!cu) return;
    setLoadingUsage(true);
    try {
      const tok = await cu.getIdToken();
      const response = await fetch("/api/usage/getMy", {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (response.ok) {
        const j = await response.json();
        setUsage({
          storageBytes: Number(j.storageBytes || 0),
          objectCount: Number(j.objectCount || 0),
          updatedAt: j.updatedAt || null,
        });
      } else {
        setUsage({ storageBytes: 0, objectCount: 0, updatedAt: null });
      }
    } catch {
      setUsage({ storageBytes: 0, objectCount: 0, updatedAt: null });
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  const fetchMfa = useCallback(async () => {
    if (!uid) {
      setMfaState({ loading: false, enabled: false });
      return;
    }
    setMfaErr("");
    setMfaMsg("");
    setMfaState((s) => ({ ...s, loading: true }));
    try {
      const profile = await loadMfaProfile(uid);
      setMfaState({ loading: false, enabled: !!profile?.enabled });
    } catch (e) {
      setMfaState({ loading: false, enabled: false });
      setMfaErr(e?.message || "Unable to load MFA status.");
    }
  }, [uid]);

  useEffect(() => { if (uid) fetchUsage(); }, [uid, fetchUsage]);
  useEffect(() => { fetchMfa(); }, [fetchMfa]);

  const plan = useMemo(() => {
    const planMeta = getPlanFromProfile(user);
    const limitBytes = getStorageLimitBytes(user);
    return {
      id: planMeta?.key || "basic",
      name: planMeta?.name || "Basic",
      limitBytes,
      storageGB: planMeta?.storageGB || 0,
      accountManagers: planMeta?.accountManagers || 0,
      priceCents: planMeta?.priceCents || 0,
      currency: planMeta?.currency || "EUR",
    };
  }, [user]);

  const currentPeriodStartMs =
    timestampToMs(user?.subscription?.currentPeriodStart) || user?.billing?.lastPaidAtMs || 0;
  const currentPeriodEndMs =
    timestampToMs(user?.subscription?.currentPeriodEnd) ||
    (currentPeriodStartMs ? currentPeriodStartMs + YEAR_MS : 0);
  const msRemaining = currentPeriodEndMs ? currentPeriodEndMs - Date.now() : 0;
  const daysRemaining = Math.max(0, Math.floor(msRemaining / DAY_MS));
  const cancellationScheduled = subscriptionState.cancelAtPeriodEnd === true;
  const cancellationDateMs = cancellationScheduled
    ? timestampToMs(subscriptionState.cancelAt) || currentPeriodEndMs
    : null;
  const cancellationDateLabel = cancellationDateMs ? new Date(cancellationDateMs).toLocaleString() : null;

  const proratedCreditCents = useMemo(() => {
    if (!plan.priceCents) return 0;
    return Math.max(0, Math.round((plan.priceCents * daysRemaining) / 365));
  }, [plan.priceCents, daysRemaining]);

  const upgradeOptions = useMemo(() => {
    return PLAN_CATALOG.filter((p) => (p.priceCents || 0) > (plan.priceCents || 0)).map((p) => {
      const appliedCredit = Math.min(proratedCreditCents, p.priceCents || 0);
      const due = Math.max(0, (p.priceCents || 0) - appliedCredit);
      return {
        ...p,
        creditCents: appliedCredit,
        dueCents: due,
      };
    });
  }, [plan.priceCents, proratedCreditCents]);

    const retentionOfferUsed = !!user?.retentionOfferUsed || !!user?.retentionOfferUsedAt;
  const retentionOptions = useMemo(() => {
    return PLAN_CATALOG.map((p) => ({ ...p, halfPriceCents: Math.round((p.priceCents || 0) / 2) }));
  }, []);

  useEffect(() => {
    if (!upgradeOpen) setUpgradeErr("");
  }, [upgradeOpen]);

  useEffect(() => {
    if (!showRetentionOffer) setRetentionErr("");
  }, [showRetentionOffer]);


  const pct = plan.limitBytes > 0 ? Math.min(100, Math.round((usage.storageBytes / plan.limitBytes) * 100)) : 0;
  const storageAtLimit = plan.limitBytes > 0 && usage.storageBytes >= plan.limitBytes;

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e?.preventDefault?.();
    setSaving(true);
    setMsg("");
    setErr("");

    try {
      await saveUserProfileSafe(
        {
          name: (form.name || "").trim(),
          displayName: (form.name || "").trim(),
          email: (form.email || "").trim(),
          phone: (form.phone || "").trim(),
          phoneNumber: (form.phone || "").trim(),
          address: (form.address || "").trim(),
        },
        { readOnly }
      );

      if (onUpdate) {
        await Promise.resolve(onUpdate({
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: form.address,
        }));
      }
      setMsg("Profile saved");
    } catch (e2) {
      setErr(e2?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  }

  const isManagerReadOnlyView = !!user?.viewingAsManager || readOnly;

  async function disableMfa() {
    if (mfaBusy || isManagerReadOnlyView) return;
    setMfaErr("");
    setMfaMsg("");
    setMfaBusy(true);
    try {
      const cu = auth.currentUser;
      if (!cu) throw new Error("Please sign in again.");

      // Require step-up before allowing disable (prevents session hijack from turning MFA off).
      if (typeof window !== "undefined" && typeof window.requireMfaStepUp === "function") {
        const ok = await window.requireMfaStepUp();
        if (!ok) {
          throw new Error("Please verify the code in the MFA pop-up, then click Disable again.");
        }
      }

      // Hard disable: clear secrets + recovery + trusted devices.
      await saveMfaProfile(uid, {
        enabled: false,
        secretEnc: "",
        secretBase32: "",
        backupCodeHashes: [],
        recovery: { hashes: [] },
        trustedDevices: [],
      });

      try { localStorage.removeItem(`mfaTrust:${uid}`); } catch {}
      clearSessionBypass(uid);

      setMfaMsg("Two-factor authentication disabled.");
      await fetchMfa();
    } catch (e) {
      setMfaErr(e?.message || "Could not disable MFA.");
    } finally {
      setMfaBusy(false);
    }
  }

  function handleMfaToggle() {
    if (isManagerReadOnlyView) return;
    if (mfaState.enabled) {
      disableMfa();
      return;
    }
    // Enabling requires setup (QR/recovery codes) → send to Security page.
    navigate("/security");
  }

  async function handleCancelSubscription() {
    if (cancelBusy) return;
    setCancelErr("");
    setCancelMsg("");
    setCancelBusy(true);
    try {
      const cu = auth.currentUser;
      if (!cu) throw new Error("Please sign in again to manage your subscription.");
      const tok = await cu.getIdToken();
      const resp = await fetch("/api/subscription/cancel", {
        method: "POST",
        headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.error) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      const scheduledMs = typeof data.cancelAtMs === "number" ? data.cancelAtMs : cancellationDateMs;
      setSubscriptionState((s) => ({
        ...s,
        cancelAt: scheduledMs || s.cancelAt || null,
        cancelAtPeriodEnd: true,
        status: data.status || s.status || "canceled",
      }));
      setCancelMsg(
        `Cancellation scheduled. Your content will be deleted at the end of your current term${
          scheduledMs ? ` on ${new Date(scheduledMs).toLocaleString()}` : ""
        }.`
      );
      setShowCancelConfirm(false);
    } catch (e) {
      setCancelErr(e?.message || "Failed to schedule cancellation.");
    } finally {
      setCancelBusy(false);
    }
  }

  async function handleResumeSubscription() {
    if (cancelBusy) return;
    setCancelErr("");
    setCancelMsg("");
    setCancelBusy(true);
    try {
      const cu = auth.currentUser;
      if (!cu) throw new Error("Please sign in again to manage your subscription.");
      const tok = await cu.getIdToken();
      const resp = await fetch("/api/subscription/resume", {
        method: "POST",
        headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.error) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      setSubscriptionState((s) => ({
        ...s,
        cancelAt: null,
        cancelAtPeriodEnd: false,
        status: data.status || s.status || "active",
      }));
      setCancelMsg("Subscription re-activated. Your account will stay live.");
      setShowCancelConfirm(false);
    } catch (e) {
      setCancelErr(e?.message || "Failed to re-activate your subscription.");
    } finally {
      setCancelBusy(false);
    }
  }


  async function handleSendResetLink() {
    if (resetBusy) return;
    setResetErr("");
    setResetMsg("");

    try {
      const cu = auth.currentUser;
      if (!cu) throw new Error("Please sign in again to send a reset link.");
      if (!cu.email) throw new Error("Your account is missing an email address.");

      setResetBusy(true);

      const token = await cu.getIdToken(true);
      const resp = await fetch("/api/auth/sendResetSelf", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.error) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }

      setResetMsg(`Password reset email sent to ${cu.email}.`);
    } catch (e) {
      setResetErr(e?.message || "Failed to send password reset email.");
    } finally {
      setResetBusy(false);
    }
  }

  async function handleDeleteAccount() {
    setDelErr("");
    setDelBusy(true);
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not signed in");
      const tok = await u.getIdToken();
      const r = await fetch("/api/deleteAccount", {
        method: "POST",
        headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Delete failed");
      try { await auth.signOut(); } catch {}
      window.location.assign("/login");
    } catch (e) {
      setDelErr(e.message || "Could not delete account");
      setDelBusy(false);
    }
}

  async function startUpgrade(targetPlan) {
    if (!targetPlan) return;
    setUpgradeErr("");
    setUpgradeBusy(targetPlan.key);
    try {
      const cu = auth.currentUser;
      if (!cu) throw new Error("Please sign in again to continue.");
      const token = await cu.getIdToken(true);
      const resp = await fetch("/api/upgrade/start", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ targetPlan: targetPlan.key }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.url) {
        throw new Error(data?.error || "Upgrade failed. Please try again.");
      }
      window.location.assign(data.url);
    } catch (e) {
      setUpgradeErr(e?.message || "Failed to start upgrade.");
    } finally {
      setUpgradeBusy("");
    }
  }

  async function startRetentionOffer(targetPlan) {
    if (!targetPlan || retentionBusy) return;
    setRetentionErr("");
    setRetentionBusy(targetPlan.key);
    try {
      const cu = auth.currentUser;
      if (!cu) throw new Error("Please sign in again to continue.");
      const token = await cu.getIdToken(true);
      const resp = await fetch("/api/subscription/retention-offer", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ targetPlan: targetPlan.key }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.error) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      if (data.url) {
        window.location.assign(data.url);
        return;
      }
      throw new Error("Unable to start checkout.");
    } catch (e) {
      setRetentionErr(e?.message || "Could not start the retention offer.");
    } finally {
      setRetentionBusy("");
    }
  }

  function handleCancelClick() {
    if (cancellationScheduled) {
      handleResumeSubscription();
      return;
    }
    if (!retentionOfferUsed) {
      setShowCancelConfirm(false);
      setShowRetentionOffer(true);
    } else {
      setShowCancelConfirm(true);
    }
  }



  return (
    <div className="pf-wrap">
      <header className="pf-header">
        <div className="pf-title">Account</div>
        <div className="pf-statuses">
          <span className={"pf-badge " + (user?.emailVerified ? "pf-badge--ok" : "")}>
            {user?.emailVerified ? "Verified email" : "Unverified email"}
          </span>
          {user?.isAdmin ? <span className="pf-badge pf-badge--admin">Admin</span> : null}
          <span className="pf-badge">Plan: {plan.name}</span>
        </div>
      </header>

            {msg && <div className="pf-alert pf-alert--ok">{msg}</div>}
      {err && <div className="pf-alert pf-alert--err">{err}</div>}

      <form className="pf-grid" onSubmit={handleSave}>
        <label className="pf-row">
          <span className="pf-label">Full name</span>
          <input className="pf-input" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Your full name" />
        </label>
        <label className="pf-row">
          <span className="pf-label">Email</span>
          <input className="pf-input" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="name@example.com" />
        </label>
        <label className="pf-row">
          <span className="pf-label">Phone</span>
          <input className="pf-input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="+353 01 234 5678" />
        </label>
        <label className="pf-row" style={{ gridColumn: "1 / -1" }}>
          <span className="pf-label">Address</span>
          <textarea className="pf-input pf-textarea" rows={3} value={form.address} onChange={(e) => setField("address", e.target.value)} placeholder="Street, City, Postcode" />
        </label>

        <div className="pf-actions" style={{ gridColumn: "1 / -1" }}>
          <button type="submit" className="pf-btn pf-btn--primary" disabled={saving || readOnly}>
            {saving ? "Saving…" : (readOnly ? "Read-only" : "Save changes")}
          </button>
          <button type="button" className="pf-btn" onClick={onLogout && onLogout}>Log out</button>
          <button type="button" className="pf-btn pf-btn--danger" onClick={() => setShowDel(true)}>Delete account</button>
        </div>
      </form>

      <section className="pf-card pf-security-card">
        <div className="pf-card-head" style={{ alignItems: "center" }}>
          <div>
            <div className="pf-card-title">Two-factor authentication</div>
            <div className="pf-subtle">
              {mfaState.loading
                ? "Checking your security settings…"
                : mfaState.enabled
                  ? "Enabled — you'll be asked for a code on sign-in (unless you trust this device)."
                  : "Off — turn this on to protect your account."}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {mfaBusy ? <span className="pf-subtle">Working…</span> : null}
            <button
              type="button"
              className={"pf-switch" + (mfaState.enabled ? " is-on" : "")}
              role="switch"
              aria-checked={mfaState.enabled}
              aria-label={mfaState.enabled ? "Disable two-factor authentication" : "Enable two-factor authentication"}
              onClick={handleMfaToggle}
              disabled={mfaState.loading || mfaBusy || isManagerReadOnlyView}
            >
              <span className="pf-switch-knob" />
            </button>
          </div>
        </div>

        {mfaMsg ? <div className="pf-alert pf-alert--ok" style={{ marginTop: 10 }}>{mfaMsg}</div> : null}
        {mfaErr ? <div className="pf-alert pf-alert--err" style={{ marginTop: 10 }}>{mfaErr}</div> : null}

        <div className="pf-security-footer">
          <button type="button" className="pf-btn pf-btn--ghost" onClick={() => navigate("/security")}>Open Security</button>
          {mfaState.enabled ? (
            <span className="pf-subtle">Tip: disabling MFA requires verification.</span>
          ) : (
            <span className="pf-subtle">You'll set up an authenticator app and download recovery codes.</span>
          )}
        </div>

        {isManagerReadOnlyView ? (
          <div className="pf-alert" style={{ marginTop: 10 }}>
            Two-factor settings are locked while you're in account manager read-only mode.
          </div>
        ) : null}
      </section>


      <section className="pf-card">
        <div className="pf-card-head">
          <div className="pf-card-title">Storage usage</div>
          <div className="pf-subtle">
            {formatBytes(usage.storageBytes)} / {formatBytes(plan.limitBytes)} ({pct}%)
          </div>
        </div>
        <div className="pf-progress"><div className="pf-progress-bar" style={{ width: `${pct}%` }} /></div>
            {storageAtLimit && (
          <div className="pf-alert pf-alert--err" style={{ marginTop: 10 }}>
            You’ve reached the storage included in your package. Upgrade to unlock more space.
            <div className="pf-actions" style={{ marginTop: 8 }}>
              <button className="pf-btn pf-btn--primary" type="button" onClick={() => setUpgradeOpen(true)}>
                Upgrade package
              </button>
            </div>
          </div>
        )}
        <div className="pf-progress-footer">
          <span className="pf-subtle">
            {usage.updatedAt
              ? `Updated: ${new Date(usage.updatedAt.seconds ? usage.updatedAt.seconds * 1000 : usage.updatedAt).toLocaleString()}`
              : "Updated periodically"}
          </span>
          <button type="button" className="pf-btn pf-btn--ghost" onClick={fetchUsage} disabled={loadingUsage}>
            {loadingUsage ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </section>

<section className="pf-card pf-plan-card">
        <div className="pf-card-head">
          <div>
            <div className="pf-card-title">Current plan</div>
            <div className="pf-subtle">
              {plan.name} • {plan.storageGB} GB storage • {plan.accountManagers} account
              manager{plan.accountManagers === 1 ? "" : "s"}
            </div>
            {daysRemaining > 0 ? (
              <div className="pf-subtle">Approximately {daysRemaining} day{daysRemaining === 1 ? "" : "s"} remaining</div>
            ) : null}
          </div>
          <button
            type="button"
            className="pf-btn pf-btn--primary"
            onClick={() => { setUpgradeOpen(true); }}
            disabled={!upgradeOptions.length}
          >
            Upgrade package
          </button>
        </div>
        {upgradeOptions.length === 0 ? (
          <p className="pf-subtle" style={{ marginTop: 8 }}>You're already on the highest plan.</p>
        ) : (
          <p className="pf-subtle" style={{ marginTop: 8 }}>
            Need more storage or account managers? Upgrade at any time and only pay the difference.
          </p>
        )}
      </section>
<section className="pf-card">
        <div className="pf-card-head" style={{ alignItems: "center" }}>
          <div>
            <div className="pf-card-title">Subscription status</div>
            <div className="pf-subtle">
              {cancellationScheduled
                ? "Cancellation is scheduled at the end of your current contract term."
                : "Cancel renewal at the end of your current term."}
            </div>
            {cancellationDateLabel ? (
              <div className="pf-subtle" style={{ marginTop: 6 }}>
                Content scheduled for deletion on {cancellationDateLabel}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className={cancellationScheduled ? "pf-btn pf-btn--primary" : "pf-btn pf-btn--danger"}
            onClick={handleCancelClick}
            disabled={cancelBusy}
          >
            {cancelBusy
              ? "Working…"
              : cancellationScheduled
                ? "Make account live again"
                : "Cancel subscription"}
          </button>
        </div>
        {showCancelConfirm && !cancellationScheduled ? (
          <div className="pf-alert pf-alert--err" style={{ marginTop: 10 }}>
            <p style={{ margin: "0 0 8px" }}>
              Your content will be permanently deleted from our servers at the end of your current contract term.
            </p>
            <div className="pf-actions">
              <button
                className="pf-btn pf-btn--danger"
                type="button"
                onClick={handleCancelSubscription}
                disabled={cancelBusy}
              >
                {cancelBusy ? "Scheduling…" : "Confirm cancellation"}
              </button>
              <button
                className="pf-btn pf-btn--ghost"
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelBusy}
              >
                Keep account live
              </button>
            </div>
          </div>
        ) : null}
        {cancelMsg ? <div className="pf-alert pf-alert--ok" style={{ marginTop: 10 }}>{cancelMsg}</div> : null}
        {cancelErr ? <div className="pf-alert pf-alert--err" style={{ marginTop: 10 }}>{cancelErr}</div> : null}
      </section>

      <section className="pf-card pf-reset-card">
        <div className="pf-reset">
          <div>
            <div className="pf-reset-title">Reset password</div>
            <p className="pf-reset-desc">Send yourself a password reset link.</p>
          </div>
          <button type="button" className="pf-btn pf-btn--ghost" onClick={handleSendResetLink} disabled={resetBusy}>
            {resetBusy ? "Sending…" : "Send reset link"}
          </button>
        </div>
        {resetMsg ? <div className="pf-alert pf-alert--ok pf-reset-alert">{resetMsg}</div> : null}
        {resetErr ? <div className="pf-alert pf-alert--err pf-reset-alert">{resetErr}</div> : null}
      </section>

      {!user?.emailVerified ? (
        <VerifyEmailBanner />
      ) : null}

      <style>{`
        .pf-wrap{max-width:900px;margin:16px auto;padding:0 16px}
        .pf-header{display:flex;align-items:center;justify-content:space-between;margin:8px 0 6px}
        .pf-title{font-size:22px;font-weight:800;color:#111827}
        .pf-statuses{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px}
        .pf-badge{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;border:1px solid #e5e7eb;background:#f9fafb;font-size:12px;color:#374151}
        .pf-badge--ok{background:#ecfdf5;border-color:#a7f3d0;color:#065f46}
        .pf-badge--admin{background:#f3e8ff;border-color:#d8b4fe;color:#6b21a8}
        .pf-plan-card{background:#f9fafb}
        .pf-card{border:1px solid #e5e7eb;border-radius:14px;padding:12px 14px;background:#fff;margin-bottom:14px}
        .pf-card-head{display:flex;align-items:baseline;justify-content:space-between}
        .pf-card-title{font-weight:700;color:#111827}
        .pf-subtle{color:#6b7280;font-size:12px}
        .pf-progress{height:10px;border-radius:999px;background:#f3f4f6;overflow:hidden;margin:10px 0}
        .pf-progress-bar{height:100%;background:linear-gradient(90deg,#4f46e5,#7c3aed)}
        .pf-progress-footer{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap}
        .pf-grid{display:grid;grid-template-columns:1fr;gap:14px}
        @media(min-width:720px){.pf-grid{grid-template-columns:1fr 1fr}}
        .pf-row{display:flex;flex-direction:column;gap:6px}
        .pf-label{color:#6b7280;font-size:13px;font-weight:600}
        .pf-input,.pf-btn{
          -webkit-appearance:none;
          appearance:none;
          box-shadow:none !important;
          text-shadow:none !important;
          background-image:none !important;
          filter:none;
        }
        .pf-input:focus,.pf-btn:focus{
          outline:2px solid transparent;
          outline-offset:2px;
          box-shadow:none !important;
          text-shadow:none !important;
          background-image:none !important;
        }
        .pf-input{height:40px;padding:0 12px;border:1px solid:#d6d6e7;border-radius:10px}
        .pf-textarea{height:auto;padding:10px 12px}
        .pf-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:8px}
        .pf-btn{height:40px;padding:0 14px;border-radius:10px;border:1px solid:#cfcfe6;background:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:flex-start;text-align:left}
        .pf-btn--primary{background:#4f46e5;color:#fff;border-color:#4f46e5}
        .pf-btn--danger{background:#dc2626;color:#fff;border-color:#dc2626}
        .pf-btn--ghost{background:transparent;border-color:#e5e7eb;color:#374151}
        .pf-btn:disabled{opacity:.6;cursor:not-allowed}
        .pf-alert{padding:10px 12px;border-radius:10px;border:1px solid #e5e7eb;background:#f9fafb}
        .pf-alert--ok{background:#ecfdf5;border-color:#a7f3d0;color:#065f46}
        .pf-alert--err{background:#fee2e2;border-color:#fecaca;color:#7a0b0b}
        .pf-reset-card{margin-top:16px}
        .pf-reset{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;flex-wrap:wrap}
        .pf-reset-title{font-weight:600;color:#111827;font-size:15px}
        .pf-reset-desc{margin:4px 0 0;color:#6b7280;font-size:13px}
        .pf-reset-alert{margin-top:12px}
        .pf-modal{position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;align-items:flex-start;justify-content:center;overflow:auto;padding:40px 16px;z-index:1200}
        .pf-modal-card{width:100%;max-width:900px;background:#fff;border-radius:18px;padding:20px;border:1px solid #e5e7eb;box-shadow:0 25px 70px rgba(15,23,42,.25)}
        .pf-modal-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px}
        .pf-modal-head h3{margin:0;color:#111827;font-size:20px}
        .pf-modal-head p{margin:6px 0 0;color:#4b5563;font-size:14px;max-width:560px}
        .pf-upgrade-grid{margin-top:18px;display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px}
        .pf-upgrade-card{position:relative;border:1px solid #e5e7eb;border-radius:14px;padding:16px;background:#fff;display:flex;flex-direction:column;gap:10px;box-shadow:0 10px 30px rgba(15,23,42,.08)}
        .pf-upgrade-badge{position:absolute;top:12px;right:12px;background:#eef2ff;color:#312e81;border-radius:999px;font-size:11px;font-weight:700;padding:4px 10px}
        .pf-upgrade-card h4{margin:0;color:#111827;font-size:18px}
        .pf-upgrade-list{margin:0;padding:0;list-style:none;color:#4b5563;font-size:13px;display:flex;flex-direction:column;gap:4px}
        .pf-upgrade-price{margin-top:auto;display:flex;align-items:center;justify-content:space-between;gap:12px}
        .pf-upgrade-due{font-size:24px;font-weight:800;color:#111827}
        .pf-upgrade-credit{display:flex;flex-direction:column;align-items:flex-end;font-size:13px;color:#059669}

        .pf-security-footer{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-top:12px}
        .pf-switch{width:54px;height:32px;border-radius:999px;border:1px solid #e5e7eb;background:#f3f4f6;display:inline-flex;align-items:center;padding:3px;cursor:pointer;position:relative;transition:background .18s ease, border-color .18s ease}
        .pf-switch:disabled{opacity:.6;cursor:not-allowed}
        .pf-switch .pf-switch-knob{width:26px;height:26px;border-radius:999px;background:#fff;border:1px solid #e5e7eb;box-shadow:0 8px 16px rgba(15,23,42,.12);transform:translateX(0);transition:transform .18s ease}
        .pf-switch.is-on{background:linear-gradient(90deg,#4f46e5,#7c3aed);border-color:#4f46e5}
        .pf-switch.is-on .pf-switch-knob{transform:translateX(22px);border-color:rgba(255,255,255,.6)}
      `}</style>
      {showDel && (
        <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000}}>
          <div style={{width:"100%", maxWidth:520, background:"#fff", borderRadius:12, border:"1px solid #e5e7eb", boxShadow:"0 20px 50px rgba(0,0,0,.2)", padding:18}}>
            <h3 style={{ fontSize:18, fontWeight:900, margin:"0 0 8px" }}>Delete account</h3>
            <p style={{ color:"#374151", margin:"0 0 10px" }}>
              This will permanently delete your account and <strong>all associated content</strong> from our systems.
              This action is <strong>irreversible</strong>.
            </p>
            {!!delErr && <div className="pf-alert pf-alert--err" style={{ marginBottom: 10 }}>{delErr}</div>}
            <div className="pf-actions">
              <button className="pf-btn pf-btn--danger" onClick={handleDeleteAccount} disabled={delBusy}>
                {delBusy ? "Deleting…" : "Yes, delete my account"}
              </button>
              <button className="pf-btn pf-btn--ghost" onClick={() => setShowDel(false)} disabled={delBusy}>Cancel</button>
            </div>
          </div>
        </div>
      )}

{showRetentionOffer && (
        <div className="pf-modal" role="dialog" aria-modal="true">
          <div className="pf-modal-card">
            <div className="pf-modal-head">
              <div>
                <h3>Stay with 50% off</h3>
                <p>
                  Choose any plan below and get <strong>50% off for the next year</strong>. This one-time offer is limited to your account
                  and is available before you finish cancelling.
                </p>
              </div>
              <button className="pf-btn pf-btn--ghost" onClick={() => setShowRetentionOffer(false)}>Close</button>
            </div>

            <div className="pf-upgrade-grid">
              {retentionOptions.map((opt) => (
                <article key={opt.key} className="pf-upgrade-card">
                  {opt.highlight && <div className="pf-upgrade-badge">Most popular</div>}
                  <h4>{opt.name}</h4>
                  <div className="pf-subtle" style={{ marginBottom: 6 }}>{opt.blurb}</div>
                  {Array.isArray(opt.features) && opt.features.length ? (
                    <ul className="pf-upgrade-list">
                      {opt.features.map((feat, i) => (
                        <li key={i}>{feat}</li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="pf-upgrade-price">
                    <div>
                      <div className="pf-upgrade-due">{centsToCurrency(opt.halfPriceCents, opt.currency)}</div>
                      <div className="pf-subtle">One-time stay offer (50% off {centsToCurrency(opt.priceCents, opt.currency)})</div>
                    </div>
                  </div>
                  <button
                    className="pf-btn pf-btn--primary"
                    disabled={!!retentionBusy && retentionBusy !== opt.key}
                    onClick={() => startRetentionOffer(opt)}
                  >
                    {retentionBusy === opt.key
                      ? "Starting…"
                      : `Keep ${opt.name} for ${centsToCurrency(opt.halfPriceCents, opt.currency)}`}
                  </button>
                </article>
              ))}
            </div>

            {retentionErr && <div className="pf-alert pf-alert--err" style={{ marginTop: 12 }}>{retentionErr}</div>}

            <div className="pf-alert pf-alert--err" style={{ marginTop: 14 }}>
              <p style={{ margin: "0 0 8px" }}>
                Your content will be permanently deleted from our servers at the end of your current contract term.
              </p>
              <div className="pf-actions">
                <button
                  className="pf-btn pf-btn--danger"
                  type="button"
                  onClick={() => {
                    setShowRetentionOffer(false);
                    setShowCancelConfirm(true);
                  }}
                  disabled={cancelBusy}
                >
                  Continue cancellation
                </button>
                <button className="pf-btn pf-btn--ghost" type="button" onClick={() => setShowRetentionOffer(false)} disabled={cancelBusy}>
                  Keep account live
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {upgradeOpen && (
        <div className="pf-modal" role="dialog" aria-modal="true">
          <div className="pf-modal-card">
            <div className="pf-modal-head">
              <div>
                <h3>Upgrade your package</h3>
                <p>
                  We credit the unused portion of your current plan ({centsToCurrency(plan.priceCents, plan.currency)} ÷ 365 × remaining days)
                  and charge the difference today. Future renewals will be at the regular price of the plan you pick.
                </p>
              </div>
              <button className="pf-btn pf-btn--ghost" onClick={() => setUpgradeOpen(false)}>Close</button>
            </div>
            {upgradeOptions.length === 0 ? (
              <div className="pf-alert" style={{ marginTop: 10 }}>
                There are no higher tiers available. You're already on the top plan.
              </div>
            ) : (
              <div className="pf-upgrade-grid">
                {upgradeOptions.map((opt) => (
                  <article key={opt.key} className="pf-upgrade-card">
                    {opt.highlight && <div className="pf-upgrade-badge">Most popular</div>}
                    <h4>{opt.name}</h4>
                    <div className="pf-subtle" style={{ marginBottom: 6 }}>{opt.blurb}</div>
                    <ul className="pf-upgrade-list">
                      <li>{opt.storageGB} GB secure storage</li>
                      <li>{opt.accountManagers} account manager{opt.accountManagers === 1 ? "" : "s"}</li>
                      <li>Includes all {plan.name} features</li>
                    </ul>
                    <div className="pf-upgrade-price">
                      <div>
                        <div className="pf-upgrade-due">{centsToCurrency(opt.dueCents, opt.currency)}</div>
                        <div className="pf-subtle">Pay today</div>
                      </div>
                      <div className="pf-upgrade-credit">
                        <span>Credit applied</span>
                        <strong>-{centsToCurrency(opt.creditCents, opt.currency)}</strong>
                      </div>
                    </div>
                    <button
                      className="pf-btn pf-btn--primary"
                      disabled={!!upgradeBusy && upgradeBusy !== opt.key}
                      onClick={() => startUpgrade(opt)}
                    >
                      {upgradeBusy === opt.key ? "Starting…" : `Upgrade to ${opt.name}`}
                    </button>
                  </article>
                ))}
              </div>
            )}
            {upgradeErr && <div className="pf-alert pf-alert--err" style={{ marginTop: 12 }}>{upgradeErr}</div>}
          </div>
        </div>
      )}

    </div>
  );
}
